import { LitElement, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Modal } from 'bootstrap';

// This component needs Bootstrap CSS for the input/button/etc. so it is not Shadow DOM encapsulated.
@customElement('new-post')
export class NewPost extends LitElement {
  @property({ type: String })
  placeholder = '';

  @property({ type: String })
  username = '';

  @property({ type: Boolean })
  canPost = true;

  @property({ type: Boolean })
  confirmBeforePost = true;

  @state()
  sendDisabled = true;

  @state()
  allowSubmit = false;

  warningModal!: Modal;

  @query('textarea') _input!: HTMLTextAreaElement;
  @query('#acknowledgement') _acknowledgement!: HTMLInputElement;

  protected override createRenderRoot() {
    return this;
  }

  renderAttachmentsLink() {
    return html`<a href="#" @click="${this.attachAFile}">
      <i class="fa-solid fa-paperclip"></i> Attach a file
    </a>`;
  }

  // TODO: Extract this to another component.
  renderWarningModal(title: string, warning: string, acknowledgement: string) {
    return html` <!-- Modal -->
      <div
        class="modal fade"
        id="warningModal"
        tabindex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h1 class="modal-title fs-5" id="exampleModalLabel">${title}</h1>
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              <div
                class="alert alert-warning d-flex align-items-center"
                role="alert"
              >
                <i class="fa-solid fa-triangle-exclamation"></i>&nbsp;
                <div>${warning}</div>
              </div>
              <input
                class="form-check-input"
                type="checkbox"
                id="acknowledgement"
                @click="${() => (this.allowSubmit = !this.allowSubmit)}"
              />
              <label class="form-check-label" for="acknowledgement">
                ${acknowledgement}
              </label>
            </div>
            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                ?disabled="${!this.allowSubmit}"
                @click="${this.send}"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>`;
  }

  override render() {
    const title = 'Post to General Information';
    const warningMsg =
      'General Information posts are visible to all registered/participating suppliers on the RFP.';
    const acknowledgementMsg =
      'I acknowledge information contained in this post is public and nonpropietary.';

    return html` <style>
        new-post {
          display: block;
        }

        new-post textarea {
          margin-bottom: 7px;
        }

        .bi {
          width: 1em;
          height: 1em;
          vertical-align: -0.125em;
          fill: currentcolor;
        }
      </style>
      <div>${this.username}</div>
      <div>
        <textarea
          class="form-control textarea__control"
          placeholder="${this.placeholder}"
          rows="1"
          @input="${this.inputChanged}"
        ></textarea>
        <div class="mt-2 d-flex justify-content-between align-items-center">
          <div>${this.renderAttachmentsLink()}</div>
          <button
            type="button"
            class="btn btn-primary"
            ?disabled="${!this.canPost || this.sendDisabled}"
            @click="${this.sendPostClicked}"
          >
            Send Post
          </button>
        </div>
      </div>
      ${this.renderWarningModal(title, warningMsg, acknowledgementMsg)}`;
  }

  protected override firstUpdated(): void {
    const maxHeight = 25 * 5;

    // This automatically resizes the textarea to fit the text. It won't go larger than the max height specified above.
    this._input.addEventListener(
      'input',
      () => {
        this._input.style.height = '0';
        this._input.style.height = `${Math.min(
          this._input.scrollHeight,
          maxHeight,
        )}px`;
      },
      false,
    );

    // Get a reference to the modal so we can show/hide it.
    this.warningModal = new Modal('#warningModal', {});
  }

  // Actions
  attachAFile(event: Event) {
    event.preventDefault();
    const attach = new CustomEvent('toggle-attach', {
      bubbles: true,
    });

    this.dispatchEvent(attach);
  }

  inputChanged() {
    this.sendDisabled = !this._input.value;
  }

  sendPostClicked() {
    if (this.confirmBeforePost) {
      this.warningModal.show();
    } else {
      this.send();
    }
  }

  send() {
    this.warningModal.hide();

    const newPost = new CustomEvent('send', {
      bubbles: true,
      detail: { text: this._input.value },
    });

    this.dispatchEvent(newPost);

    // Reset the status of the modal and input. The input should be empty, the checkbox should be unchecked.
    this._input.value = '';
    this._acknowledgement.checked = false;
    this.allowSubmit = false;
    this.inputChanged();
  }
}

export interface FileUploadOutputFiles {
  uploadId: string;
  name: string;
}

export type SendEvent = CustomEvent<{
  files: Array<FileUploadOutputFiles>;
  giPostId?: number;
  sellerId: number;
  text: string;
  threadId?: number;
}>;
